import { useState, useCallback, useMemo, useEffect } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import ModalBasicLayout from "../ModalBasicLayout";
import ModalMedia from "../ModalMedia";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_BATCH_IMAGE_EDIT_STATUS_ERROR,
  MODAL_BATCH_IMAGE_EDIT_STATUS_PENDING,
  MODAL_BATCH_IMAGE_EDIT_STATUS_SUCCESS,
  MODAL_BATCH_IMAGE_EDIT_TITLE,
  MODAL_BATCH_IMAGE_EDIT_VERSION_LABEL,
  MODAL_BATCH_IMAGE_EDIT_SAVE_ALERT_TITLE,
  MODAL_BATCH_IMAGE_EDIT_SAVE_ALERT_DESCRIPTION,
  MODAL_BATCH_IMAGE_EDIT_SAVE_AS_NEW_BUTTON,
  MODAL_BATCH_IMAGE_EDIT_REPLACE_BUTTON,
  MODAL_IMAGE_EDIT_PROCESSING_MESSAGE,
} from "../../../i18n/keysTranslations";
import { useChatMessages, useBatchImageEdit, BATCH_ITEM_STATUS } from "./hooks";
import {
  ChatSidebar,
  ResponsiveSidebarContainer,
  SaveContentActions,
} from "./shared";
import { BatchChatMessages, BatchImageGrid } from "./batch";
import { getTileDisplayState } from "./batch/tileDisplayState";

const ModalBatchImageEdit = ({
  isOpen,
  onClose,
  initialItems = [],
  onCallbackApply = () => {},
  showSaveAlert = true,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedSelectionId, setExpandedSelectionId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const {
    chatMessages,
    addMessage,
    replaceThinkingMessage,
    setChatMessages,
    chatScrollRef,
  } = useChatMessages();

  const {
    items,
    visibleVersion,
    setVisibleVersion,
    isJobInProgress,
    isGenerateInProgress,
    retryingSelectionIds,
    generate,
    retryItem,
    hasApplyableSuccesses,
    getSuccessfulItemsForApply,
    getVisibleEntry,
  } = useBatchImageEdit({ initialItems, isOpen });

  useEffect(() => {
    if (isOpen) {
      setChatMessages([]);
      setIsSidebarOpen(true);
      setExpandedSelectionId(null);
    }
  }, [isOpen, setChatMessages]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const onCloseModal = useCallback(() => {
    setExpandedSelectionId(null);
    onClose();
  }, [onClose]);

  const onExpand = useCallback((selectionId) => {
    setExpandedSelectionId(selectionId);
  }, []);

  const onCloseExpand = useCallback(() => {
    setExpandedSelectionId(null);
  }, []);

  const createThinkingMessage = useCallback(
    () => ({
      id: (Date.now() + 1).toString(),
      role: "assistant",
      content: t(MODAL_IMAGE_EDIT_PROCESSING_MESSAGE),
      timestamp: new Date(),
      isThinking: true,
    }),
    [t]
  );

  const onSendMessage = useCallback(
    async (prompt, attachmentUrls = [], attachmentFileNames = {}) => {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt || isJobInProgress) return;

      const resolvedAttachmentUrls = Array.isArray(attachmentUrls)
        ? attachmentUrls
        : [];

      addMessage({
        id: Date.now().toString(),
        role: "user",
        content: trimmedPrompt,
        timestamp: new Date(),
        attachmentUrls: resolvedAttachmentUrls,
        attachmentFileNames: attachmentFileNames || {},
      });
      addMessage(createThinkingMessage());

      const result = await generate(
        trimmedPrompt,
        resolvedAttachmentUrls,
        attachmentFileNames
      );

      if (result?.cancelled) {
        setChatMessages((prev) =>
          prev.filter((message) => !message.isThinking)
        );
        return;
      }

      if (result?.succeeded && result.targetVersion) {
        replaceThinkingMessage({
          id: (Date.now() + 2).toString(),
          role: "assistant",
          content: t(MODAL_BATCH_IMAGE_EDIT_VERSION_LABEL, {
            version: result.targetVersion,
          }),
          timestamp: new Date(),
          version: result.targetVersion,
        });
        return;
      }

      replaceThinkingMessage({
        id: (Date.now() + 2).toString(),
        role: "assistant",
        content: t(MODAL_BATCH_IMAGE_EDIT_STATUS_ERROR),
        timestamp: new Date(),
        isError: true,
      });
    },
    [
      addMessage,
      createThinkingMessage,
      generate,
      isJobInProgress,
      replaceThinkingMessage,
      setChatMessages,
      t,
    ]
  );

  const onVersionSelect = useCallback(
    (version) => {
      setVisibleVersion(Number(version));
    },
    [setVisibleVersion]
  );

  const onApply = useCallback(
    async ({ mode }) => {
      const successfulItems = getSuccessfulItemsForApply();
      if (successfulItems.length === 0) return;

      await onCallbackApply({ items: successfulItems, mode });
      onCloseModal();
    },
    [getSuccessfulItemsForApply, onCallbackApply, onCloseModal]
  );

  const expandedItem = expandedSelectionId
    ? items.find((item) => item.selectionId === expandedSelectionId)
    : null;
  const expandedImageUrl = expandedItem
    ? getTileDisplayState(expandedItem, visibleVersion).imageUrl
    : null;
  const isExpandedView = Boolean(expandedImageUrl);

  const gridStatusAnnouncement = useMemo(() => {
    const counts = items.reduce(
      (acc, itemState) => {
        const status =
          getVisibleEntry(itemState)?.status || BATCH_ITEM_STATUS.SUCCESS;

        if (status === BATCH_ITEM_STATUS.PENDING) {
          acc.pending += 1;
        } else if (status === BATCH_ITEM_STATUS.ERROR) {
          acc.error += 1;
        } else {
          acc.success += 1;
        }

        return acc;
      },
      { pending: 0, success: 0, error: 0 }
    );

    if (isGenerateInProgress) {
      return t(MODAL_BATCH_IMAGE_EDIT_STATUS_PENDING);
    }

    const parts = [];

    if (counts.success > 0) {
      parts.push(
        `${counts.success} ${t(MODAL_BATCH_IMAGE_EDIT_STATUS_SUCCESS)}`
      );
    }

    if (counts.error > 0) {
      parts.push(`${counts.error} ${t(MODAL_BATCH_IMAGE_EDIT_STATUS_ERROR)}`);
    }

    if (counts.pending > 0) {
      parts.push(
        `${counts.pending} ${t(MODAL_BATCH_IMAGE_EDIT_STATUS_PENDING)}`
      );
    }

    return parts.join(", ");
  }, [getVisibleEntry, isGenerateInProgress, items, t]);

  return (
    <>
      <ModalBasicLayout
        modalOpen={isOpen}
        onCloseModal={onCloseModal}
        fullScreen
        maxWidth={false}
        fullWidth
        hasTitle={false}
        ariaLabel={t(MODAL_BATCH_IMAGE_EDIT_TITLE)}
        sxContent={{
          py: 0,
          px: 0,
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
        sxPaper={{
          height: "100vh",
          maxHeight: "100vh",
        }}
      >
        <ResponsiveSidebarContainer
          isSmallScreen={isSmallScreen}
          open={isSidebarOpen}
          onClose={toggleSidebar}
        >
          {isSidebarOpen ? (
            <ChatSidebar
              isSmallScreen={isSmallScreen}
              title={t(MODAL_BATCH_IMAGE_EDIT_TITLE)}
              isProcessing={isJobInProgress}
              showSendLoading={isGenerateInProgress}
              onSendMessage={onSendMessage}
              onToggleSidebar={toggleSidebar}
              scrollRef={chatScrollRef}
            >
              <BatchChatMessages
                messages={chatMessages}
                visibleVersion={visibleVersion}
                onVersionSelect={onVersionSelect}
              />
            </ChatSidebar>
          ) : null}
        </ResponsiveSidebarContainer>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <BatchImageGrid
            items={items}
            visibleVersion={visibleVersion}
            isGenerateInProgress={isGenerateInProgress}
            retryingSelectionIds={retryingSelectionIds}
            isSidebarOpen={isSidebarOpen}
            gridStatusAnnouncement={gridStatusAnnouncement}
            onExpand={onExpand}
            onRetry={retryItem}
            onToggleSidebar={toggleSidebar}
            onClose={onCloseModal}
          />

          <SaveContentActions
            disabled={!hasApplyableSuccesses || isJobInProgress}
            showSaveAlert={showSaveAlert}
            onSave={onApply}
            onCloseAfterSave={onCloseModal}
            saveAlertTitleKey={MODAL_BATCH_IMAGE_EDIT_SAVE_ALERT_TITLE}
            saveAlertDescriptionKey={
              MODAL_BATCH_IMAGE_EDIT_SAVE_ALERT_DESCRIPTION
            }
            saveAsNewButtonKey={MODAL_BATCH_IMAGE_EDIT_SAVE_AS_NEW_BUTTON}
            replaceButtonKey={MODAL_BATCH_IMAGE_EDIT_REPLACE_BUTTON}
          />
        </Box>
      </ModalBasicLayout>

      {isExpandedView ? (
        <ModalMedia
          modalOpen={isExpandedView}
          onCloseModal={onCloseExpand}
          url={expandedImageUrl}
          showEditButton={false}
          showDownloadButton
          showShareButton={false}
          showDeleteButton={false}
          isPicker
        />
      ) : null}
    </>
  );
};

export default ModalBatchImageEdit;
